/**
 * src/shell/gallery/GalleryChannelView.tsx
 *
 * Sovereign Fine Art Gallery & Showroom Channel View.
 * Bound to Domain InventoryService with Virtualized Art Grid,
 * Provenance Timelines, Lightbox a11y, and Curator Metadata Modals.
 */

import React, { useState, useMemo } from 'react';
import { useInventory } from '../hooks/useInventory';
import { ArtworkProduct } from '../../domain/inventory/types/product-types';
import { ArtworkCard } from './ArtworkCard';
import { Lightbox } from './Lightbox';
import { ArtworkEditModal } from './ArtworkEditModal';
import { GalleryWorkspace } from './GalleryWorkspace';
import { GalleryIcon } from '../icons';

interface GalleryChannelViewProps {
  onOpenActivation?: () => void;
}

export const GalleryChannelView: React.FC<GalleryChannelViewProps> = ({ onOpenActivation }) => {
  const { artworks, loading, refresh } = useInventory('superadmin');

  const [activeTab, setActiveTab] = useState<'showroom' | 'workspace'>('showroom');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedium, setSelectedMedium] = useState<string>('all');
  const [inspectingArtwork, setInspectingArtwork] = useState<ArtworkProduct | null>(null);
  const [editingArtwork, setEditingArtwork] = useState<ArtworkProduct | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [originBtn, setOriginBtn] = useState<HTMLElement | null>(null);

  // Available mediums for filtering
  const mediums = useMemo(() => {
    const set = new Set<string>();
    artworks.forEach((art) => {
      if (art.metadata.medium) set.add(art.metadata.medium);
    });
    return Array.from(set);
  }, [artworks]);

  // Filtered artworks
  const filteredArtworks = useMemo(() => {
    return artworks.filter((art) => {
      if (selectedMedium !== 'all' && art.metadata.medium !== selectedMedium) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = art.title.toLowerCase().includes(q);
        const matchArtist = art.metadata.artist.toLowerCase().includes(q);
        if (!matchTitle && !matchArtist) return false;
      }
      return true;
    });
  }, [artworks, selectedMedium, searchQuery]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        overflowY: 'auto',
      }}
    >
      {/* Top Header Strip */}
      <div
        style={{
          padding: '12px 20px',
          borderBottom: '1px solid var(--theme-border, #1e293b)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          backgroundColor: 'var(--theme-bg-header, #0f172a)',
          backdropFilter: 'blur(8px)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--theme-accent, #38bdf8)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GalleryIcon size={18} color="var(--theme-accent, #38bdf8)" style={{ flexShrink: 0 }} />
            <span>FINE ART GALLERY</span>
          </div>

          {/* View Mode Switch */}
          <div style={{ display: 'flex', backgroundColor: 'var(--theme-bg-input, #1e293b)', borderRadius: '8px', padding: '2px', border: '1px solid var(--theme-border, #1e293b)' }}>
            <button
              onClick={() => setActiveTab('showroom')}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: activeTab === 'showroom' ? 'var(--theme-accent, #38bdf8)' : 'transparent',
                color: activeTab === 'showroom' ? '#090d16' : 'var(--theme-text-muted, #94a3b8)',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Exhibition Grid ({artworks.length})
            </button>
            <button
              onClick={() => setActiveTab('workspace')}
              style={{
                padding: '4px 12px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: activeTab === 'workspace' ? 'var(--theme-accent, #38bdf8)' : 'transparent',
                color: activeTab === 'workspace' ? '#090d16' : 'var(--theme-text-muted, #94a3b8)',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Media Lightbox Workspace
            </button>
          </div>
        </div>

        {activeTab === 'showroom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Search */}
            <input
              type="text"
              placeholder="Search artist or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '12px',
                width: '200px',
              }}
            />

            {/* Medium Filter */}
            <select
              value={selectedMedium}
              onChange={(e) => setSelectedMedium(e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-bg-input, #1e293b)',
                border: '1px solid var(--theme-border, #1e293b)',
                color: 'var(--theme-text-primary, #f8fafc)',
                fontSize: '12px',
              }}
            >
              <option value="all">All Mediums</option>
              {mediums.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            {/* Add Artwork Button */}
            <button
              onClick={(e) => {
                setOriginBtn(e.currentTarget);
                setEditingArtwork(null);
                setIsEditModalOpen(true);
              }}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: '#090d16',
                border: 'none',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>➕</span>
              <span>New Artwork</span>
            </button>
          </div>
        )}
      </div>

      {/* Main View Area */}
      {activeTab === 'workspace' ? (
        <div style={{ flex: 1, padding: '24px' }}>
          <GalleryWorkspace />
        </div>
      ) : (
        <div style={{ flex: 1, padding: '24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
              Loading fine art collection from domain inventory...
            </div>
          ) : filteredArtworks.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px',
                backgroundColor: 'rgba(30, 41, 59, 0.3)',
                borderRadius: '16px',
                border: '1px dashed #334155',
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🖼️</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc', marginBottom: '4px' }}>
                No Artworks Found
              </div>
              <div style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '16px' }}>
                Register your first fine art piece or adjust your search filter.
              </div>
              <button
                onClick={() => {
                  setEditingArtwork(null);
                  setIsEditModalOpen(true);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  backgroundColor: '#38bdf8',
                  color: '#090d16',
                  border: 'none',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ➕ Register Artwork
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: '24px',
              }}
            >
              {filteredArtworks.map((art) => (
                <ArtworkCard
                  key={art.id}
                  artwork={art}
                  onInspect={(a) => setInspectingArtwork(a)}
                  onEdit={(a) => {
                    setEditingArtwork(a);
                    setIsEditModalOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Accessible Lightbox */}
      <Lightbox
        artwork={inspectingArtwork}
        isOpen={!!inspectingArtwork}
        onClose={() => setInspectingArtwork(null)}
        originElement={originBtn}
      />

      {/* Curator Form Modal */}
      <ArtworkEditModal
        artwork={editingArtwork}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
};
